import { useState } from 'react';
import { useDelete, useOne } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router';
import { Button, Space, Tag } from 'antd';
import { DetailField, DetailPage, useConfirmAction } from '@viq-admin/ui';
import type { ToolkitConfig } from '@viq-admin/types';
import { Can } from '../../ability/AbilityContext';
import { ToolkitsEdit } from './edit';
import { AppliedConferencesModal } from './AppliedConferencesModal';

/** Config-key → human label, moved here from the retired `show.tsx`. */
const FIELD_LABELS: Record<string, string> = {
  url: 'API URL',
  base_url: 'Base URL',
  evt_uid: 'Event UID',
  filled_url: 'Filled URL',
  login_url: 'Login URL',
  data_extract_url: 'Data Extract URL',
  show_id: 'Show ID',
  eventId: 'Event ID',
  exhibitorId: 'Exhibitor ID',
};

/**
 * Toolkit config detail — read-only by default, Edit flips the form in place
 * (via the shared DetailPage). One route, /toolkits/:id. Mirrors
 * CompanyDetail/IntegrationDetail's structure; see CompanyDetail's header
 * comment for the 404-derivation rationale (a missing record surfaces
 * through useOne as `query.error` with `statusCode === 404`, never an empty
 * success).
 *
 * Toolkit configs are global reusable templates — there is no archive
 * lifecycle here (unlike companies). The read view additionally surfaces the
 * secret's PRESENCE (a Tag) — something only visible inside Edit before this
 * page existed — but never the value, mirroring the edit form's masked-input
 * semantics. `record.config` never carries secrets (the GET response is
 * write-only for those), but the render below defensively skips a `secrets`
 * key anyway.
 *
 * "View conferences" opens the existing AppliedConferencesModal (unchanged)
 * and is NOT capability-gated — it's a read action, and the modal's own
 * mutations (detach / reapply) are gated internally on `manage Toolkit`.
 * Delete is relocated from the old edit-page header, gated the same way.
 */
export function ToolkitDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [conferencesOpen, setConferencesOpen] = useState(false);
  const confirm = useConfirmAction();

  const { result: record, query } = useOne<ToolkitConfig>({
    resource: 'toolkits',
    id,
    queryOptions: { enabled: !!id },
  });
  const { mutateAsync: deleteConfig } = useDelete();

  const error = query?.error as { statusCode?: number } | null | undefined;
  const notFound = error?.statusCode === 404;

  function handleDelete() {
    confirm({
      title: 'Delete this toolkit config?',
      content:
        'Conferences already applied keep their config; this only removes the reusable template.',
      okText: 'Delete',
      danger: true,
      successMessage: 'Toolkit config deleted.',
      onConfirm: async () => {
        await deleteConfig({
          resource: 'toolkits',
          id: id!,
          // The confirm dialog above owns the success/error toast — suppress
          // Refine's own notification provider so the operator does not see
          // it twice (matches the list row's delete action / CompanyDetail).
          successNotification: false,
          errorNotification: false,
        });
        navigate('/toolkits');
      },
    });
  }

  const headerActions = (
    <Space>
      <Button onClick={() => setConferencesOpen(true)}>View conferences</Button>
      <Can I="manage" a="Toolkit" passThrough>
        {(allowed: boolean) =>
          allowed ? (
            <Button danger onClick={handleDelete}>
              Delete
            </Button>
          ) : null
        }
      </Can>
    </Space>
  );

  const readView = record ? (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="toolkit-read-view"
    >
      <DetailField label="Name" value={record.name} />
      <DetailField label="Type" value={record.toolkitType} />
      <DetailField
        label="Applied to"
        value={`${record.appliedConferenceCount} conference${
          record.appliedConferenceCount !== 1 ? 's' : ''
        }`}
      />
      <DetailField
        label="Secret"
        value={
          record.hasSecret ? <Tag color="green">Stored</Tag> : <Tag>Not set</Tag>
        }
      />
      {Object.entries(record.config)
        .filter(([k]) => k !== 'secrets')
        .map(([k, v]) => (
          <DetailField
            key={k}
            label={FIELD_LABELS[k] ?? k}
            value={typeof v === 'string' ? v : JSON.stringify(v)}
          />
        ))}
      <DetailField label="Created" value={new Date(record.createdAt).toLocaleDateString()} />
      <DetailField label="Updated" value={new Date(record.updatedAt).toLocaleDateString()} />
    </Space>
  ) : null;

  return (
    <>
      <DetailPage
        title={record?.name ?? 'Toolkit'}
        breadcrumbs={[
          { label: 'Toolkits', href: '/toolkits' },
          { label: record?.name ?? '' },
        ]}
        onNavigate={({ href }) => navigate(href)}
        onBack={() => navigate('/toolkits')}
        loading={query?.isLoading}
        error={notFound ? undefined : query?.error}
        notFound={notFound}
        notFoundHref="/toolkits"
        editing={editing}
        onEditingChange={(next) => {
          setEditing(next);
          // Leaving edit by ANY path (Cancel, back, a breadcrumb) must clear
          // `dirty` — not just the `onSaved` success path below. Otherwise a
          // dirty-then-discarded session leaves `dirty` stuck true, and the
          // NEXT edit session (freshly remounted, genuinely pristine) still
          // trips the discard guard on its first Cancel.
          if (!next) setDirty(false);
        }}
        editCap="manage:Toolkit"
        dirty={dirty}
        actions={headerActions}
        readView={readView}
        editView={
          <ToolkitsEdit
            id={id}
            onDirtyChange={setDirty}
            onSaved={() => {
              setDirty(false);
              setEditing(false);
            }}
          />
        }
      />
      {conferencesOpen && (
        <AppliedConferencesModal
          configId={id!}
          open
          onClose={() => setConferencesOpen(false)}
        />
      )}
    </>
  );
}
